/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2024-03-14.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "open-business-cci-jerome-colombain-ia-entreprises-2024",
  "title": "Open Business CCI : Jérôme Colombain et l'IA en ouverture du cycle 2024",
  "excerpt": "Le 12 mars 2024, la CCI Saône-Doubs devait accueillir à la Maison de l'économie le journaliste Jérôme Colombain pour une soirée Open Business sur l'impact de l'IA, avec les dirigeants de Livdeo, Quarks et Scoder en grands témoins.",
  "seoTitle": "Open Business CCI : Jérôme Colombain et l'IA",
  "seoDescription": "Le 12 mars 2024, la CCI Saône-Doubs recevait le journaliste Jérôme Colombain pour une soirée sur l'impact de l'IA en entreprise, avec Livdeo, Quarks et Scoder.",
  "category": "ia",
  "tags": [
    "Open Business",
    "CCI Saône-Doubs",
    "Jérôme Colombain",
    "Maison de l'économie",
    "ChatGPT"
  ],
  "author": "jimmy",
  "publishedAt": "2024-03-14",
  "updatedAt": "2024-03-14",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-la-cci-et-le-lycee-jules-haag.jpg",
    "credit": "Photo : Besançon, la CCI et le lycée Jules Haag — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Le bâtiment moderne et vitré de la CCI Saône-Doubs à Besançon, la Maison de l'économie, vu depuis les hauteurs, avec le lycée Jules Haag à l'arrière-plan"
  },
  "content": [
    {
      "type": "lead",
      "text": "Mardi 12 mars, [la CCI Saône-Doubs](/blog/cybersecday-2025-besancon-cci-ia-cyber) devait ouvrir son cycle Open Business 2024, à la Maison de l'économie de Besançon, par une soirée consacrée à l'intelligence artificielle. Au programme annoncé : un exposé du journaliste Jérôme Colombain, longtemps voix du numérique sur France Info, retraçant l'histoire de l'IA jusqu'à ChatGPT, puis une table ronde avec trois entreprises régionales. Un rendez-vous ouvert aux dirigeants du bassin, quinze mois après l'irruption des agents conversationnels dans le débat public."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Quoi",
          "value": "Conférence Open Business « L'impact de l'intelligence artificielle sur votre entreprise », suivie d'une table ronde et d'un cocktail"
        },
        {
          "label": "Quand",
          "value": "Mardi 12 mars 2024 à 19 h"
        },
        {
          "label": "Où",
          "value": "Maison de l'économie, CCI Saône-Doubs, 46 avenue Villarceau, Besançon"
        },
        {
          "label": "Qui",
          "value": "Jérôme Colombain (journaliste tech), avec Ciprian Melian (Livdeo), Gilles Tonnelier (Quarks) et Sylvain Giampiccolo (Scoder)"
        },
        {
          "label": "Pour qui",
          "value": "Dirigeants et entreprises locales, entrée libre et gratuite"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un cycle taillé pour les dirigeants"
    },
    {
      "type": "p",
      "text": "Les Open Business ne datent pas de cette année. La CCI Saône-Doubs présente ce rendez-vous comme un « accélérateur de réflexion » qui réunit « 3 fois par an un public d'entreprises locales » pendant « 2 heures en début de soirée », avec l'ambition de sensibiliser ses ressortissants aux sujets de demain et de les aider à mieux se connaître [^4]. Le format est stable : un intervenant expose une thématique, une table ronde réunit des « grands témoins » issus d'entreprises ou de collectivités, puis un cocktail prolonge la discussion [^3]."
    },
    {
      "type": "p",
      "text": "Le 20 février, la chambre consulaire a annoncé trois soirées pour 2024 : l'intelligence artificielle le 12 mars à Besançon, le vieillissement de la population le 14 mai à Besançon avec Serge Guérin, puis la performance en entreprise et dans le sport le 19 septembre à Vesoul avec l'ancien athlète Stéphane Diagana ; le président de la CCI, Jean-Luc Quivogne, a par ailleurs indiqué qu'une quatrième conférence, prévue à Montbéliard, était « sur le point de voir le jour » [^3]. Caroline Astier, directrice générale de la CCI Saône-Doubs, résume l'intention en trois verbes : étonner par des thèmes innovants, inspirer par les témoignages de dirigeants régionaux et d'experts nationaux, connecter en réunissant les acteurs du territoire [^5]. Il s'agit, dit-elle, d'« interpeller les dirigeants d'entreprise sur les défis et enjeux de demain » [^3]."
    },
    {
      "type": "h2",
      "text": "De l'histoire de l'IA à ChatGPT"
    },
    {
      "type": "p",
      "text": "Le choix de l'intervenant n'est pas anodin. Jérôme Colombain a été journaliste à France Info de 1994 à 2021, où il a créé et animé pendant plus de vingt-cinq ans la chronique quotidienne « Nouveau Monde » consacrée à l'actualité numérique, avant de lancer le podcast indépendant *Monde Numérique* [^6]. C'est donc un vulgarisateur, plus qu'un ingénieur, que la CCI a convié."
    },
    {
      "type": "p",
      "text": "Le programme annoncé promettait un « parcours à travers l'évolution de l'intelligence artificielle, depuis ses origines jusqu'aux innovations récentes comme ChatGPT, et au-delà » [^1]. Le journaliste devait ensuite démystifier l'IA par des applications concrètes en entreprise, avant d'aborder les questions qui fâchent [^1][^2] :"
    },
    {
      "type": "ul",
      "items": [
        "les usages professionnels déjà accessibles : reconnaissance d'images, génération de contenus, meilleur accès à l'information [^1] ;",
        "les défis d'éthique, de confidentialité et de fiabilité que pose l'intégration de ces outils [^1] ;",
        "les risques associés à la désinformation, aux biais et au remplacement du travail humain [^1] ;",
        "des stratégies pour une intégration « efficace et éthique » dans les organisations [^1]."
      ]
    },
    {
      "type": "p",
      "text": "La veille, dans le Journal du Palais, Jean-Luc Quivogne avait fixé l'objectif de la soirée [^2]."
    },
    {
      "type": "quote",
      "text": "Il s'agit de proposer un aperçu des usages, actuels et futurs, de l'IA dans les entreprises, avec des exemples concrets et des recommandations pour une intégration efficace et éthique, tout en ouvrant la voie à une réflexion sur les évolutions futures de cette technologie clé.",
      "cite": "Jean-Luc Quivogne, président de la CCI Saône-Doubs, cité par le Journal du Palais [^2]"
    },
    {
      "type": "h2",
      "text": "Trois entreprises régionales à la table ronde"
    },
    {
      "type": "p",
      "text": "Après l'exposé et un échange avec l'auditoire [^1], une table ronde animée par Jérôme Colombain devait réunir trois grands témoins [^2] :"
    },
    {
      "type": "ol",
      "items": [
        "**Ciprian Melian**, directeur de [Livdeo](/blog/french-tech-night-2025-besancon-bfc20), société bisontine spécialisée dans les solutions numériques inclusives à base d'IA pour les institutions culturelles et l'industrie du tourisme [^2] ;",
        "**Gilles Tonnelier**, expert en IA chez Quarks, éditeur de solutions digitales de maîtrise des risques chimiques pour la santé et l'environnement [^2] ;",
        "**Sylvain Giampiccolo**, dirigeant de Scoder, entreprise du Doubs d'outillage, de découpage et d'emboutissage, présente dans plus de 20 pays avec un taux d'exportation de 70 % [^2]."
      ]
    },
    {
      "type": "p",
      "text": "Le panel dit quelque chose de la façon dont la CCI aborde le sujet : une société qui conçoit et vend des solutions à base d'IA, un éditeur de logiciels de maîtrise des risques chimiques qui emploie un expert en IA, un industriel exportateur. À lui seul, il illustre des degrés de maturité très différents, du concepteur à l'utilisateur potentiel. La soirée devait se conclure par le cocktail habituel du cycle [^1]."
    },
    {
      "type": "h2",
      "text": "Un signal pour le tissu économique local"
    },
    {
      "type": "p",
      "text": "Qu'une chambre de commerce inscrive l'IA en tête de son cycle de conférences, à égalité avec le vieillissement démographique ou la performance, marque un changement d'échelle : le sujet devient une question de direction générale, pas seulement de service informatique. Le Journal du Palais rappelle d'ailleurs que la réponse à l'actualité figure parmi les orientations stratégiques de la CCI Saône-Doubs [^2]. Nous n'avons trouvé, à l'heure où nous écrivons, ni compte rendu ni bilan de fréquentation ; la CCI présente son cycle comme un rendez-vous régulier de dirigeants [^4], et la soirée était annoncée gratuite et en entrée libre [^1] — une porte d'entrée peu contraignante pour des chefs d'entreprise qui hésitent encore à se saisir du sujet."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Un journaliste national pour vulgariser, trois entreprises locales pour incarner : la formule Open Business proposait aux dirigeants du bassin une grille de lecture commune de l'IA, de ses promesses à ses risques (désinformation, biais, remplacement du travail humain). Pour un [territoire industriel et microtechnique](/blog/femto-st-besancon-capitale-microtechniques) comme le nôtre, un rendez-vous ouvert et gratuit de ce type abaisse le coût d'entrée pour des dirigeants qui n'ont pas encore engagé d'expérimentation. Reste à savoir ce que la soirée aura effectivement apporté : nous y reviendrons si un compte rendu est publié."
    },
    {
      "type": "h2",
      "text": "Et maintenant ?"
    },
    {
      "type": "p",
      "text": "Le cycle Open Business se poursuivra le 14 mai avec le vieillissement de la population, puis le 19 septembre à Vesoul avec Stéphane Diagana [^3]. Sur l'intelligence artificielle, la suite se jouera dans les entreprises elles-mêmes. Faute de compte rendu public à ce jour, cet article s'appuie sur le programme annoncé et sur les entretiens préalables publiés par la presse régionale ; IA Besançon suivra les prochaines étapes du cycle."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "L'impact de l'intelligence artificielle sur votre entreprise",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/agenda/limpact-de-lintelligence-artificielle-sur-votre-entreprise/",
      "type": "press",
      "date": "2024-02-08"
    },
    {
      "id": 2,
      "title": "À Besançon, on interroge le rapport de l'IA aux entreprises",
      "publisher": "Le Journal du Palais de Bourgogne-Franche-Comté",
      "url": "https://journal-du-palais.fr/au-sommaire/entreprises/a-besancon-on-interroge-le-rapport-de-l-ia-aux-entreprises",
      "type": "press",
      "date": "2024-03-11"
    },
    {
      "id": 3,
      "title": "Open Business : les prochains rendez-vous de la CCI Saône-Doubs pour répondre aux défis et enjeux de demain",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/open-business-les-prochains-rendez-vous-de-la-cci-saone-doubs-pour-repondre-aux-defis-et-enjeux-de-demain/",
      "type": "press",
      "date": "2024-02-20"
    },
    {
      "id": 4,
      "title": "Open Business - Accélérateurs de réflexion",
      "publisher": "CCI Saône-Doubs",
      "url": "https://www.saone-doubs.cci.fr/open-business/accelerateurs-reflexion",
      "type": "official",
      "date": ""
    },
    {
      "id": 5,
      "title": "Trois Open Business pour interpeller les dirigeants sur les défis de demain",
      "publisher": "Le Journal du Palais de Bourgogne-Franche-Comté",
      "url": "https://journal-du-palais.fr/au-sommaire/collectivites/trois-open-business-pour-interpeller-les-dirigeants-sur-les-defis-de-demain",
      "type": "press",
      "date": "2024-02-22"
    },
    {
      "id": 6,
      "title": "Biographie de Jérôme Colombain",
      "publisher": "jeromecolombain.com",
      "url": "https://jeromecolombain.com/biographie/",
      "type": "web",
      "date": ""
    }
  ]
};
